import {useState} from 'react';
import {
    Button,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Select,
    Stack,
    TextInput,
} from '@mantine/core';
import {
    MdCancel,
    MdCheck,
    MdClear,
} from 'react-icons/md';

import {useTestObservationsFilter} from '../hooks/useTestObservationsFilter';
import {
    ObservationOutcome,
    TestObservationsFilter,
    VerificationResult,
} from '../types';

// Only the four fields TestObservationsFilterInput actually accepts. Nothing else
// may be sent — AppSync rejects unknown input fields, and a filter the server
// ignores is worse than no filter (it silently lies about what is in view).
const OUTCOME_OPTIONS = [
    {
        value: '',
        label: 'All outcomes'
    },
    ...Object.values(ObservationOutcome)
             .map(value => ({
                 value,
                 label: value
             })),
];

const VERIFICATION_OPTIONS = [
    {
        value: '',
        label: 'All verification results'
    },
    ...Object.values(VerificationResult)
             .map(value => ({
                 value,
                 label: value
             })),
];

export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useTestObservationsFilter();

    // Local draft so the list only refetches on Apply, not on every keystroke.
    const [draft, setDraft] = useState<TestObservationsFilter>(filters);

    const setField = (field: keyof TestObservationsFilter) => (value: string | null) =>
        setDraft(current => ({
            ...current,
            [field]: value ?? ''
        }));

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease'
            }}
            position='right'
            offset={10}
            opened={opened}
            onClose={onClose}
            title='Filter Observations'
            padding='xl'
            size='md'
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Stack gap='md' my='md'>
                <Select
                    label='Outcome'
                    description='The primary triage move: narrow to FAIL.'
                    data={OUTCOME_OPTIONS}
                    value={draft.outcome ?? ''}
                    onChange={setField('outcome')}
                    clearable={false}
                />
                <Select
                    label='Verification Result'
                    description='NOT_VERIFIED finds actions that worked but were never recorded.'
                    data={VERIFICATION_OPTIONS}
                    value={draft.verificationResult ?? ''}
                    onChange={setField('verificationResult')}
                />
                <TextInput
                    label='Action Code'
                    placeholder='e.g. user.sign-in'
                    value={draft.actionCode ?? ''}
                    onChange={(event) => setField('actionCode')(event.currentTarget.value)}
                />
                <TextInput
                    label='Synthetic User ID'
                    value={draft.syntheticUserId ?? ''}
                    onChange={(event) => setField('syntheticUserId')(event.currentTarget.value)}
                />
            </Stack>

            <Group justify='space-between' mt='xl'>
                <Button
                    variant='outline'
                    leftSection={<MdCancel/>}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Group gap='xs'>
                    <Button
                        variant='outline'
                        leftSection={<MdClear/>}
                        onClick={() => {
                            setDraft({});
                            handleClearFilters();
                        }}
                    >
                        Clear
                    </Button>
                    <Button
                        leftSection={<MdCheck/>}
                        onClick={() => {
                            handleSetFilters(draft);
                            onClose();
                        }}
                    >
                        Apply
                    </Button>
                </Group>
            </Group>
        </Drawer>
    );
};
